/** Information Security API client. Mount: /api/information-security. */
import { apiFetch } from './api';

export interface SecurityStats {
  assets: number;
  assetsHoldingPersonalData: number;
  assetsOverdueReview: number;
  openIncidents: number;
  awaitingAssessment: number;
  reportableNotReported: number;
  deadlineWithin24Hours: number;
  deadlinePassedNotReported: number;
}
export async function fetchSecurityStats(signal?: AbortSignal): Promise<SecurityStats> {
  return apiFetch<SecurityStats>('/api/information-security/stats', { method: 'GET', signal });
}

export interface DigitalAsset {
  id: string;
  reference: string;
  name: string;
  assetType: string;
  description: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  ownerIsCompany: boolean;
  custodianUserId: string | null;
  custodianName: string | null;
  classification: string;
  holdsPersonalData: boolean;
  personalDataNotes: string | null;
  location: string | null;
  supplier: string | null;
  /** Where the supplier is on the supplier register. */
  supplierSecurityId: string | null;
  /** The Approved Suppliers row behind it; exclusive with `supplier`. */
  approvedSupplierId: string | null;
  approvedSupplierName: string | null;
  criticality: string;
  status: string;
  acquiredDate: string | null;
  renewalDate: string | null;
  reviewDate: string | null;
  decommissionedDate: string | null;
  identifier: string | null;
  softwareVersion: string | null;
  endOfSupportDate: string | null;
  recoveryPriority: string;
  dependencies: string | null;
  accessNotes: string | null;
  retention: string | null;
  disposalMethod: string | null;
  disposalReference: string | null;
  notes: string | null;
  lastReviewedAt: string | null;
  lastReviewedByUserId: string | null;
  reviewIntervalMonths: number;
  updatedByUserId: string | null;
  updatedAt: string;
}
export interface DigitalAssetInput {
  name: string;
  assetType?: string;
  description?: string | null;
  ownerUserId?: string | null;
  ownerIsCompany?: boolean;
  custodianUserId?: string | null;
  reviewIntervalMonths?: number;
  classification?: string;
  holdsPersonalData?: boolean;
  personalDataNotes?: string | null;
  location?: string | null;
  supplier?: string | null;
  supplierSecurityId?: string | null;
  approvedSupplierId?: string | null;
  criticality?: string;
  status?: string;
  acquiredDate?: string | null;
  renewalDate?: string | null;
  reviewDate?: string | null;
  decommissionedDate?: string | null;
  identifier?: string | null;
  softwareVersion?: string | null;
  endOfSupportDate?: string | null;
  recoveryPriority?: string;
  dependencies?: string | null;
  accessNotes?: string | null;
  retention?: string | null;
  disposalMethod?: string | null;
  disposalReference?: string | null;
  notes?: string | null;
}
export async function fetchDigitalAssets(signal?: AbortSignal): Promise<DigitalAsset[]> {
  return apiFetch<DigitalAsset[]>('/api/information-security/assets', { method: 'GET', signal });
}
export interface ApprovedItSupplier {
  id: string;
  name: string;
  reference: string | null;
  categories: string[];
}
export async function fetchApprovedItSuppliers(signal?: AbortSignal): Promise<ApprovedItSupplier[]> {
  return apiFetch<ApprovedItSupplier[]>('/api/information-security/assets/approved-suppliers', { method: 'GET', signal });
}
export async function createDigitalAsset(body: DigitalAssetInput): Promise<DigitalAsset> {
  return apiFetch<DigitalAsset>('/api/information-security/assets', { method: 'POST', body });
}
export async function updateDigitalAsset(id: string, body: Partial<DigitalAssetInput>): Promise<DigitalAsset> {
  return apiFetch<DigitalAsset>(`/api/information-security/assets/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}
/** Confirm the entry is still right; the next review moves on by its own cycle. */
export async function reviewDigitalAsset(id: string): Promise<DigitalAsset> {
  return apiFetch<DigitalAsset>(`/api/information-security/assets/${encodeURIComponent(id)}/review`, { method: 'POST' });
}

export async function deleteDigitalAsset(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/information-security/assets/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * `icoReportability` is the ASSESSMENT - whether a report is owed. `reportedToIco`
 * is the different fact that one was made. The server refuses the second without
 * the first, so a report can never be recorded against a breach nobody assessed.
 */
export interface SecurityIncident {
  id: string;
  reference: string;
  title: string;
  description: string | null;
  category: string;
  severity: string;
  status: string;
  occurredAt: string | null;
  discoveredAt: string;
  icoDeadlineAt: string;
  hoursToDeadline: number;
  personalDataInvolved: boolean;
  peopleAffected: number | null;
  digitalAssetId: string | null;
  digitalAssetName: string | null;
  containmentActions: string | null;
  recoveryActions: string | null;
  rootCause: string | null;
  lessonsLearned: string | null;
  icoReportability: string;
  icoDecisionReason: string | null;
  icoDecidedAt: string | null;
  reportedToIco: boolean;
  icoReportedAt: string | null;
  icoReference: string | null;
  /** Art. 33(1): why the report went in after the 72 hours. */
  icoLateReason: string | null;
  /** Art. 33(3)(b): the DPO contact as given on the notification. */
  icoDpoContact: string | null;
  subjectNotification: string;
  subjectNotificationReason: string | null;
  subjectNotificationMessage: string | null;
  subjectNotifiedAt: string | null;
  closedAt: string | null;
}
export interface SecurityIncidentInput {
  title: string;
  description?: string | null;
  category?: string;
  severity?: string;
  occurredAt?: string | null;
  discoveredAt?: string;
  personalDataInvolved?: boolean;
  peopleAffected?: number | null;
  digitalAssetId?: string | null;
  containmentActions?: string | null;
}
export async function fetchSecurityIncidents(signal?: AbortSignal): Promise<SecurityIncident[]> {
  return apiFetch<SecurityIncident[]>('/api/information-security/incidents', { method: 'GET', signal });
}
/** One incident, for its own page. */
export async function fetchSecurityIncident(id: string, signal?: AbortSignal): Promise<SecurityIncident> {
  return apiFetch<SecurityIncident>(`/api/information-security/incidents/${encodeURIComponent(id)}`, { method: 'GET', signal });
}

export async function createSecurityIncident(body: SecurityIncidentInput): Promise<SecurityIncident> {
  return apiFetch<SecurityIncident>('/api/information-security/incidents', { method: 'POST', body });
}
export async function updateSecurityIncident(
  id: string,
  body: Partial<SecurityIncidentInput> & { status?: string; recoveryActions?: string | null; rootCause?: string | null; lessonsLearned?: string | null },
): Promise<SecurityIncident> {
  return apiFetch<SecurityIncident>(`/api/information-security/incidents/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}
export async function decideIcoReportability(
  id: string,
  body: { decision: 'reportable' | 'not_reportable'; reason: string },
): Promise<SecurityIncident> {
  return apiFetch<SecurityIncident>(`/api/information-security/incidents/${encodeURIComponent(id)}/ico-reportability`, { method: 'POST', body });
}
export async function recordIcoReport(
  id: string,
  body: { reportedAt?: string; icoReference?: string | null; lateReason?: string | null },
): Promise<SecurityIncident> {
  return apiFetch<SecurityIncident>(`/api/information-security/incidents/${encodeURIComponent(id)}/ico-report`, { method: 'POST', body });
}
export async function recordSubjectNotification(
  id: string,
  body: {
    decision: 'not_required' | 'required' | 'done';
    notifiedAt?: string;
    reason?: string | null;
    message?: string | null;
  },
): Promise<SecurityIncident> {
  return apiFetch<SecurityIncident>(`/api/information-security/incidents/${encodeURIComponent(id)}/subject-notification`, { method: 'POST', body });
}
export async function deleteSecurityIncident(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/information-security/incidents/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface ProcedureStep {
  number: number;
  /** The stage the Incidents tab works through: report, contain, assess, notify, recover, learn. */
  stage: string;
  title: string;
  responsible: string;
  timescale: string | null;
  detail: string[];
}
export interface ProcedureSection {
  heading: string;
  paragraphs: string[];
}
export interface IncidentProcedure {
  title: string;
  issue: number;
  issuedOn: string;
  approvedBy: string | null;
  summary: string;
  sections: ProcedureSection[];
  steps: ProcedureStep[];
}
export interface ProcedureRevision {
  revision: number;
  issue: number;
  editedByName: string | null;
  editedAt: string;
  note: string | null;
}
export interface IncidentProcedureDoc extends IncidentProcedure {
  /** False while the company is still on the platform wording. */
  edited: boolean;
  lastEditedByName: string | null;
  lastEditedAt: string | null;
  revisions: ProcedureRevision[];
}
export interface ProcedureInput {
  title?: string;
  summary?: string;
  sections?: ProcedureSection[];
  steps?: ProcedureStep[];
  issue?: number;
  issuedOn?: string | null;
  approvedBy?: string | null;
  note?: string | null;
}
export async function fetchIncidentProcedure(signal?: AbortSignal): Promise<IncidentProcedureDoc> {
  return apiFetch<IncidentProcedureDoc>('/api/information-security/procedure', { method: 'GET', signal });
}
export async function saveIncidentProcedure(body: ProcedureInput): Promise<IncidentProcedureDoc> {
  return apiFetch<IncidentProcedureDoc>('/api/information-security/procedure', { method: 'PUT', body });
}

/**
 * The quarterly access review (ISO 27001 A.5.18). A round carries one line per
 * sign-in account, categorised against the last completed round.
 */
export interface AccessReviewLine {
  id: string;
  userId: string;
  personName: string;
  personEmail: string | null;
  roleSnapshot: string;
  sectionsSnapshot: string[];
  /** What they held on the asset register when the round opened (A.5.11). */
  assetsHeld: string[];
  category: string;
  categoryReason: string | null;
  decision: string;
  decidedAt: string | null;
  note: string | null;
  accessRemovedAt: string | null;
  accessRemovalMethod: string | null;
  accessRemovalNote: string | null;
  /** True while this is a "remove" nobody has carried out. */
  removalOutstanding: boolean;
}
export interface AccessReview {
  id: string;
  reference: string;
  periodLabel: string;
  dueDate: string;
  status: string;
  openedAt: string;
  completedAt: string | null;
  notes: string | null;
  lineCount: number;
  pendingCount: number;
  joiners: number;
  movers: number;
  leavers: number;
  /** Decided "remove" and not yet carried out. A round cannot finish on these. */
  removalsOutstanding: number;
  lines?: AccessReviewLine[];
}

/** Where the browser fetches the controlled-document version from. */
export function accessReviewPdfPath(id: string): string {
  return '/api/information-security/access-reviews/' + encodeURIComponent(id) + '/pdf';
}

export async function fetchAccessReviews(signal?: AbortSignal): Promise<AccessReview[]> {
  return apiFetch<AccessReview[]>('/api/information-security/access-reviews', { method: 'GET', signal });
}
export async function fetchAccessReview(id: string, signal?: AbortSignal): Promise<AccessReview> {
  return apiFetch<AccessReview>(`/api/information-security/access-reviews/${encodeURIComponent(id)}`, { method: 'GET', signal });
}
export async function openAccessReview(body: { periodLabel?: string; dueDate?: string } = {}): Promise<AccessReview> {
  return apiFetch<AccessReview>('/api/information-security/access-reviews', { method: 'POST', body });
}
export async function decideAccessReviewLine(
  reviewId: string,
  lineId: string,
  body: { decision: 'keep' | 'change' | 'remove'; note?: string | null },
): Promise<AccessReview> {
  return apiFetch<AccessReview>(
    `/api/information-security/access-reviews/${encodeURIComponent(reviewId)}/lines/${encodeURIComponent(lineId)}`,
    { method: 'POST', body },
  );
}
/**
 * Carrying a removal out, as against deciding one is owed. `signin_disabled`
 * switches the account off here; `done_elsewhere` records that somebody did it
 * in a system we do not own, and signs their name to that.
 */
export async function recordAccessRemoval(
  reviewId: string,
  lineId: string,
  body: { method: 'signin_disabled' | 'done_elsewhere'; note?: string | null },
): Promise<AccessReview> {
  return apiFetch<AccessReview>(
    `/api/information-security/access-reviews/${encodeURIComponent(reviewId)}/lines/${encodeURIComponent(lineId)}/removal`,
    { method: 'POST', body },
  );
}
export async function completeAccessReview(id: string): Promise<AccessReview> {
  return apiFetch<AccessReview>(`/api/information-security/access-reviews/${encodeURIComponent(id)}/complete`, { method: 'POST' });
}

/**
 * Who is appointed. A job title says what somebody does; an appointment is its
 * own record, the same shape the fire marshal register uses.
 */
export interface SecurityAppointment {
  id: string;
  appointmentType: string;
  titleLabel: string | null;
  employeeId: string | null;
  holderName: string;
  holderJobTitle: string | null;
  isExternal: boolean;
  publishedContact: string | null;
  appointedOn: string;
  endsOn: string | null;
  isCurrent: boolean;
  isStatutory: boolean;
  reportsTo: string | null;
  conflictCheckedOn: string | null;
  conflictNotes: string | null;
  conflictWorthChecking: boolean;
  notes: string | null;
}
export interface SecurityAppointmentInput {
  appointmentType: string;
  titleLabel?: string | null;
  employeeId?: string | null;
  externalName?: string | null;
  publishedContact?: string | null;
  appointedOn: string;
  endsOn?: string | null;
  isStatutory?: boolean;
  reportsTo?: string | null;
  conflictCheckedOn?: string | null;
  conflictNotes?: string | null;
  notes?: string | null;
}
export interface AppointablePerson {
  id: string;
  name: string;
  jobTitle: string | null;
  titleLooksConflicted: boolean;
}

export async function fetchAppointments(signal?: AbortSignal): Promise<SecurityAppointment[]> {
  return apiFetch<SecurityAppointment[]>('/api/information-security/appointments', { method: 'GET', signal });
}
export async function fetchAppointablePeople(signal?: AbortSignal): Promise<AppointablePerson[]> {
  return apiFetch<AppointablePerson[]>('/api/information-security/appointments/people', { method: 'GET', signal });
}
export async function createAppointment(body: SecurityAppointmentInput): Promise<SecurityAppointment> {
  return apiFetch<SecurityAppointment>('/api/information-security/appointments', { method: 'POST', body });
}
export async function updateAppointment(
  id: string,
  body: Partial<SecurityAppointmentInput>,
): Promise<SecurityAppointment> {
  return apiFetch<SecurityAppointment>(`/api/information-security/appointments/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}
export async function deleteAppointment(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/api/information-security/appointments/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** The sidebar's red number: what is late or nearly late across the three registers. */
export async function fetchInformationSecurityBadge(signal?: AbortSignal): Promise<number> {
  const data = await apiFetch<{ count: number }>('/api/information-security/badge', { method: 'GET', signal });
  return data.count;
}

/* ─── The three registers beside the asset register ─────────────────────── */

export interface InformationAsset {
  id: string;
  reference: string;
  name: string;
  description: string | null;
  format: string;
  classification: string | null;
  holdsPersonalData: boolean;
  specialCategory: boolean;
  dataSubjects: string | null;
  dataCategories: string | null;
  location: string | null;
  volume: string | null;
  retention: string | null;
  disposalMethod: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  ownerIsCompany: boolean;
  criticality: string | null;
  reviewDate: string | null;
  lastReviewedAt: string | null;
  lastReviewedByName: string | null;
  reviewIntervalMonths: number;
  /** Past its review date on the company clock. */
  reviewOverdue: boolean;
  notes: string | null;
  systems: Array<{ id: string; reference: string; name: string }>;
}
export interface InformationAssetInput {
  name: string;
  description?: string | null;
  format?: string;
  classification?: string | null;
  holdsPersonalData?: boolean;
  specialCategory?: boolean;
  dataSubjects?: string | null;
  dataCategories?: string | null;
  location?: string | null;
  volume?: string | null;
  retention?: string | null;
  disposalMethod?: string | null;
  ownerUserId?: string | null;
  ownerIsCompany?: boolean;
  criticality?: string | null;
  reviewIntervalMonths?: number;
  notes?: string | null;
  systemIds?: string[];
}

export interface SecuritySupplier {
  id: string;
  reference: string;
  /** Live from Approved Suppliers when linked. */
  name: string;
  approvedSupplierId: string | null;
  parentSupplierId: string | null;
  parentSupplierName: string | null;
  whatTheyDo: string | null;
  contact: string | null;
  country: string | null;
  accessLevel: string | null;
  securityAssurance: string | null;
  riskRating: string | null;
  contractReference: string | null;
  contractEndsOn: string | null;
  isProcessor: boolean;
  dataCategories: string | null;
  dataSubjects: string | null;
  leavesUk: boolean;
  transferSafeguard: string | null;
  dpaInPlace: boolean;
  dpaSignedOn: string | null;
  dpaReference: string | null;
  startedOn: string | null;
  endedOn: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  reviewDate: string | null;
  lastReviewedAt: string | null;
  lastReviewedByName: string | null;
  reviewIntervalMonths: number;
  reviewOverdue: boolean;
  notes: string | null;
  subProcessorCount: number;
  /** A processor handling personal data with no written contract. */
  dpaMissing: boolean;
}
export interface SecuritySupplierInput {
  name: string;
  approvedSupplierId?: string | null;
  parentSupplierId?: string | null;
  whatTheyDo?: string | null;
  contact?: string | null;
  country?: string | null;
  accessLevel?: string | null;
  securityAssurance?: string | null;
  riskRating?: string | null;
  contractReference?: string | null;
  contractEndsOn?: string | null;
  isProcessor?: boolean;
  dataCategories?: string | null;
  dataSubjects?: string | null;
  leavesUk?: boolean;
  transferSafeguard?: string | null;
  dpaInPlace?: boolean;
  dpaSignedOn?: string | null;
  dpaReference?: string | null;
  startedOn?: string | null;
  endedOn?: string | null;
  ownerUserId?: string | null;
  reviewIntervalMonths?: number;
  notes?: string | null;
}

export interface DataFlow {
  id: string;
  reference: string;
  name: string;
  purpose: string | null;
  dataSubjects: string | null;
  dataCategories: string | null;
  specialCategory: boolean;
  specialCategoryNotes: string | null;
  source: string | null;
  destination: string | null;
  recipients: string | null;
  lawfulBasis: string | null;
  specialCategoryCondition: string | null;
  leavesUk: boolean;
  transferSafeguard: string | null;
  retention: string | null;
  securityMeasures: string | null;
  ownerUserId: string | null;
  ownerName: string | null;
  reviewDate: string | null;
  lastReviewedAt: string | null;
  lastReviewedByName: string | null;
  reviewIntervalMonths: number;
  notes: string | null;
  /** In order: what the data passes through. The diagram's arrows. */
  assets: Array<{ id: string; reference: string; name: string; position: number }>;
  suppliers: Array<{ id: string; reference: string; name: string; isProcessor: boolean }>;
  informationAssets: Array<{ id: string; reference: string; name: string }>;
  reviewOverdue: boolean;
  conditionMissing: boolean;
  safeguardMissing: boolean;
}
export interface DataFlowInput {
  name: string;
  purpose?: string | null;
  dataSubjects?: string | null;
  dataCategories?: string | null;
  specialCategory?: boolean;
  specialCategoryNotes?: string | null;
  source?: string | null;
  destination?: string | null;
  recipients?: string | null;
  lawfulBasis?: string | null;
  specialCategoryCondition?: string | null;
  leavesUk?: boolean;
  transferSafeguard?: string | null;
  retention?: string | null;
  securityMeasures?: string | null;
  ownerUserId?: string | null;
  reviewIntervalMonths?: number;
  notes?: string | null;
  assetIds?: string[];
  supplierIds?: string[];
  informationAssetIds?: string[];
}

const INFO = '/api/information-security/information-assets';
const SUPP = '/api/information-security/suppliers';
const FLOW = '/api/information-security/data-flows';

/** Opens in the browser to read, print or save. */
export function registerPdfPath(
  which: 'assets' | 'information-assets' | 'suppliers' | 'data-flows' | 'incidents' | 'appointments',
): string {
  return `/api/information-security/${which}/pdf`;
}
export async function fetchInformationAssets(signal?: AbortSignal): Promise<InformationAsset[]> {
  return apiFetch<InformationAsset[]>(INFO, { method: 'GET', signal });
}
export async function createInformationAsset(body: InformationAssetInput): Promise<InformationAsset> {
  return apiFetch<InformationAsset>(INFO, { method: 'POST', body });
}
export async function updateInformationAsset(id: string, body: Partial<InformationAssetInput>): Promise<InformationAsset> {
  return apiFetch<InformationAsset>(`${INFO}/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}
export async function reviewInformationAsset(id: string): Promise<InformationAsset> {
  return apiFetch<InformationAsset>(`${INFO}/${encodeURIComponent(id)}/review`, { method: 'POST' });
}
export async function deleteInformationAsset(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`${INFO}/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function fetchSecuritySuppliers(signal?: AbortSignal): Promise<SecuritySupplier[]> {
  return apiFetch<SecuritySupplier[]>(SUPP, { method: 'GET', signal });
}
export async function createSecuritySupplier(body: SecuritySupplierInput): Promise<SecuritySupplier> {
  return apiFetch<SecuritySupplier>(SUPP, { method: 'POST', body });
}
export async function updateSecuritySupplier(id: string, body: Partial<SecuritySupplierInput>): Promise<SecuritySupplier> {
  return apiFetch<SecuritySupplier>(`${SUPP}/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}
export async function reviewSecuritySupplier(id: string): Promise<SecuritySupplier> {
  return apiFetch<SecuritySupplier>(`${SUPP}/${encodeURIComponent(id)}/review`, { method: 'POST' });
}
export async function deleteSecuritySupplier(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`${SUPP}/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function fetchDataFlows(signal?: AbortSignal): Promise<DataFlow[]> {
  return apiFetch<DataFlow[]>(FLOW, { method: 'GET', signal });
}
export async function createDataFlow(body: DataFlowInput): Promise<DataFlow> {
  return apiFetch<DataFlow>(FLOW, { method: 'POST', body });
}
export async function updateDataFlow(id: string, body: Partial<DataFlowInput>): Promise<DataFlow> {
  return apiFetch<DataFlow>(`${FLOW}/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}
export async function reviewDataFlow(id: string): Promise<DataFlow> {
  return apiFetch<DataFlow>(`${FLOW}/${encodeURIComponent(id)}/review`, { method: 'POST' });
}
export async function deleteDataFlow(id: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`${FLOW}/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
